import { AudienceCredentialAdAccount, AudienceCredentialTypeDetail } from 'cio/models/audience-credential';
import CamelCaseTransform from 'cio/transforms/camel-case';
import { AdNetworkType } from 'cio/utils/ad-audiences';
import { isEmpty } from '@ember/utils';

interface SerializedAudienceCredentialAdAccount {
  name: string | null;
  account_id: string | number | null;
}

interface SerializedAudienceCredentialTypeDetail {
  ad_accounts: SerializedAudienceCredentialAdAccount[];
}

function generateNameWithId(account: AudienceCredentialAdAccount, type: AdNetworkType) {
  if (type === 'facebook') {
    return generateFacebookNameWithId(account);
  }

  if (type === 'adwords') {
    return generateAdwordsNameWithId(account);
  }

  throw new Error(`Unknown ad network type: ${type}`);
}

function generateFacebookNameWithId(account: AudienceCredentialAdAccount) {
  if (!isEmpty(account.name)) {
    return `${account.accountId} - ${account.name}`;
  }

  return account.accountId;
}

function generateAdwordsNameWithId(account: AudienceCredentialAdAccount) {
  let accountIdFormatted = '';

  if (account.accountId != null) {
    let id = String(account.accountId);
    accountIdFormatted = id.replace(/(\w{3})(\w{3})(\w)/, '$1-$2-$3');
  }

  if (!isEmpty(account.name)) {
    return `${accountIdFormatted} - ${account.name}`;
  }

  return accountIdFormatted;
}

export default class AudienceCredentialTypeDetailTransform extends CamelCaseTransform {
  deserialize(serialized: SerializedAudienceCredentialTypeDetail | null, options: any) {
    let deserialized = super.deserialize(serialized, { ...options, recursive: true });

    if (deserialized == null) {
      return {};
    }

    deserialized.adAccounts = (deserialized.adAccounts ?? []).map((account: AudienceCredentialAdAccount) => {
      let nameWithId = generateNameWithId(account, options.type);

      return {
        ...account,
        accountId: String(account.accountId),
        nameWithId,
      };
    });

    return deserialized;
  }

  serialize(deserialized: AudienceCredentialTypeDetail | null, options: any) {
    let serialized = super.serialize(deserialized, { ...options, recursive: true });

    if (serialized == null) {
      return {};
    }

    serialized.ad_accounts = (serialized.ad_accounts ?? []).map((account: SerializedAudienceCredentialAdAccount) => {
      let a: any = { ...account };
      delete a.name_with_id;
      return a;
    });

    return serialized;
  }
}

declare module 'ember-data/types/registries/transform' {
  export default interface TransformRegistry {
    'audience-credential/type-detail': AudienceCredentialTypeDetailTransform | null;
  }
}
